import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, Trash2, Lock, QrCode, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/sonic`;

const SonicAdmin = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [artifacts, setArtifacts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (authenticated) {
      fetchArtifacts();
    }
  }, [authenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('password', password);
      await axios.post(`${API}/admin/verify`, formData);
      setAuthenticated(true);
      setError('');
    } catch (err) {
      setError('Invalid password');
    }
  };

  const fetchArtifacts = async () => {
    try {
      const response = await axios.get(`${API}/artifacts`);
      setArtifacts(response.data);
    } catch (error) {
      console.error('Error fetching artifacts:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.title) {
      setError('Please provide a title and audio file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      if (uploadForm.description) {
        formData.append('description', uploadForm.description);
      }

      await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess('Artifact uploaded successfully!');
      setUploadForm({ title: '', description: '', file: null });
      document.getElementById('file-input').value = '';
      fetchArtifacts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error uploading artifact');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this artifact?')) {
      return;
    }

    try {
      await axios.delete(`${API}/artifacts/${id}`);
      setSuccess('Artifact deleted successfully');
      fetchArtifacts();
    } catch (err) {
      setError('Error deleting artifact');
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="bg-charcoal border-2 border-copper rounded-xl p-8">
            <div className="flex justify-center mb-6">
              <Lock className="h-16 w-16 text-copper" />
            </div>
            <h1 
              className="text-3xl font-black text-copper text-center mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              ADMIN ACCESS
            </h1>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 bg-charcoal border-2 border-copper/30 rounded-lg text-off-white placeholder-off-white/50 focus:border-copper focus:outline-none mb-4"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              />
              {error && (
                <p className="text-burgundy text-sm mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  {error}
                </p>
              )}
              <button
                type="submit"
                className="w-full px-6 py-3 bg-copper hover:bg-copper-dark text-charcoal font-bold rounded-lg transition-all"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                UNLOCK
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 
          className="text-5xl md:text-6xl font-black text-copper mb-12"
          style={{ fontFamily: 'Unbounded, sans-serif' }}
        >
          SONIC ADMIN
        </h1>

        {/* Upload Form */}
        <div className="bg-charcoal border-2 border-copper rounded-xl p-8 mb-12">
          <h2 
            className="text-2xl font-bold text-off-white mb-6 flex items-center gap-3"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            <Upload className="h-6 w-6 text-copper" />
            UPLOAD ARTIFACT
          </h2>
          
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label 
                className="block text-off-white mb-2 font-semibold"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Title *
              </label>
              <input
                type="text"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                className="w-full px-4 py-3 bg-charcoal border-2 border-copper/30 rounded-lg text-off-white focus:border-copper focus:outline-none"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
                required
              />
            </div>

            <div>
              <label 
                className="block text-off-white mb-2 font-semibold"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Description
              </label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                className="w-full px-4 py-3 bg-charcoal border-2 border-copper/30 rounded-lg text-off-white focus:border-copper focus:outline-none"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
                rows="3"
              />
            </div>

            <div>
              <label 
                className="block text-off-white mb-2 font-semibold"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Audio File * (WAV, MP3, OGG)
              </label>
              <input
                id="file-input"
                type="file"
                accept="audio/*"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                className="w-full px-4 py-3 bg-charcoal border-2 border-copper/30 rounded-lg text-off-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-copper file:text-charcoal file:font-semibold hover:file:bg-copper-dark cursor-pointer"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
                required
              />
            </div>

            {error && (
              <div className="p-4 bg-burgundy/20 border border-burgundy rounded-lg text-burgundy" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 bg-copper/20 border border-copper rounded-lg text-copper flex items-center gap-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                <CheckCircle className="h-5 w-5" />
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={uploading}
              className="w-full px-6 py-4 bg-copper hover:bg-copper-dark text-charcoal font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              {uploading ? 'UPLOADING...' : 'UPLOAD ARTIFACT'}
            </button>
          </form>
        </div>

        {/* Artifacts List */}
        <div>
          <h2 
            className="text-2xl font-bold text-off-white mb-6"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            UPLOADED ARTIFACTS ({artifacts.length})
          </h2>
          
          {artifacts.length === 0 ? (
            <div 
              className="text-center py-12 text-off-white/60"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              No artifacts uploaded yet
            </div>
          ) : (
            <div className="space-y-4">
              {artifacts.map((artifact) => (
                <div
                  key={artifact.id}
                  className="bg-charcoal border-2 border-copper/30 rounded-lg p-6 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <h3 
                      className="text-xl font-bold text-off-white mb-1"
                      style={{ fontFamily: 'Unbounded, sans-serif' }}
                    >
                      {artifact.title}
                    </h3>
                    {artifact.description && (
                      <p 
                        className="text-off-white/70 text-sm"
                        style={{ fontFamily: 'Rajdhani, sans-serif' }}
                      >
                        {artifact.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => navigate(`/sonic/play/${artifact.id}`)}
                      className="px-4 py-2 bg-copper hover:bg-copper-dark text-charcoal font-semibold rounded-lg transition-all flex items-center gap-2"
                      style={{ fontFamily: 'Rajdhani, sans-serif' }}
                    >
                      <QrCode className="h-4 w-4" />
                      VIEW
                    </button>
                    <button
                      onClick={() => handleDelete(artifact.id)}
                      className="px-4 py-2 bg-burgundy hover:bg-burgundy/80 text-off-white font-semibold rounded-lg transition-all flex items-center gap-2"
                      style={{ fontFamily: 'Rajdhani, sans-serif' }}
                    >
                      <Trash2 className="h-4 w-4" />
                      DELETE
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SonicAdmin;